import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Shield,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  FileText,
  TrendingUp,
  Wallet,
  CreditCard,
  RefreshCw,
  ChevronRight,
  ExternalLink,
  Lock,
  Unlock,
  Home,
  User,
  Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Status badge component
function StatusBadge({ status }) {
  const configs = {
    open: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", label: "OPEN" },
    active: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", label: "ACTIVE" },
    pending_close: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", label: "PENDING CLOSE" },
    closed: { bg: "bg-white/5", border: "border-white/10", text: "text-white/50", label: "CLOSED" }
  };
  const config = configs[status] || configs.open;
  
  return (
    <span className={`px-2 py-1 text-xs font-bold rounded ${config.bg} ${config.border} border ${config.text}`}>
      {config.label}
    </span>
  );
}

// Disbursement approval modal
function DisbursementApprovalModal({ disbursement, isOpen, onClose, onApprove, onReject }) {
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const handleApprove = async () => {
    setProcessing(true);
    await onApprove(disbursement.transaction_id, notes);
    setProcessing(false);
    onClose();
  };

  const handleReject = async () => {
    setProcessing(true);
    await onReject(disbursement.transaction_id, notes);
    setProcessing(false);
    onClose();
  };

  if (!disbursement) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/[0.08] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
            Disbursement Approval Required
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Property */}
          <div className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
            <Home className="w-5 h-5 text-white/50" />
            <div>
              <p className="text-xs text-white/50">Property</p>
              <p className="text-white font-medium">{disbursement.property_address || disbursement.deal_id}</p>
            </div>
          </div>

          {/* Amount */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
              <p className="text-xs text-white/50">Amount</p>
              <p className="text-xl font-bold text-white">{formatCurrency(disbursement.amount)}</p>
            </div>
            <div className="p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
              <p className="text-xs text-white/50">Platform Fee</p>
              <p className="text-lg font-bold text-white/70">{formatCurrency(disbursement.platform_fee)}</p>
            </div>
            <div className="p-3 bg-emerald-500/[0.05] rounded-lg border border-emerald-500/20">
              <p className="text-xs text-emerald-400/70">Net Payout</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(disbursement.net_payout)}</p>
            </div>
          </div>

          {/* Recipient */}
          <div className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
            <User className="w-5 h-5 text-white/50" />
            <div className="flex-1">
              <p className="text-xs text-white/50">Recipient ({disbursement.recipient_type})</p>
              <p className="text-white font-medium">{disbursement.recipient_name}</p>
              {disbursement.recipient_email && (
                <p className="text-xs text-white/40">{disbursement.recipient_email}</p>
              )}
            </div>
          </div>

          {/* Payment Type */}
          <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
            <span className="text-white/50">Payment Type</span>
            <span className="text-white font-medium capitalize">{disbursement.payment_type?.replace(/_/g, ' ')}</span>
          </div>

          {/* Description */}
          {disbursement.description && (
            <div className="p-3 bg-white/[0.02] rounded-lg border border-white/[0.06]">
              <p className="text-xs text-white/50 mb-1">Description</p>
              <p className="text-white/70 text-sm">{disbursement.description}</p>
            </div>
          )}

          {/* Wallet Balance Warning */}
          {disbursement.escrow_balance !== undefined && disbursement.amount > disbursement.escrow_balance && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <p className="text-red-400 text-sm">
                Insufficient wallet balance. Available: {formatCurrency(disbursement.escrow_balance)}
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-sm text-white/50 mb-2 block">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add approval/rejection notes..."
              className="bg-black/40 border-white/[0.08] text-white"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/[0.08] text-white/70"
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            disabled={processing}
            className="bg-red-600 hover:bg-red-500 text-white"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Reject
          </Button>
          <Button
            onClick={handleApprove}
            disabled={processing || (disbursement.escrow_balance !== undefined && disbursement.amount > disbursement.escrow_balance)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {processing ? "Processing..." : "Approve Release"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TitleEscrowDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [pendingDisbursements, setPendingDisbursements] = useState([]);
  const [selectedDisbursement, setSelectedDisbursement] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchStripeStatus();
    fetchPendingDisbursements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deal-wallet/title/dashboard`, {
        withCredentials: true
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load deal wallet dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deal-wallet/stripe/connect/status`, {
        withCredentials: true
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch Stripe status:", error);
    }
  };

  const fetchPendingDisbursements = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deal-wallet/disbursements/pending`, {
        withCredentials: true
      });
      setPendingDisbursements(response.data.disbursements || []);
    } catch (error) {
      console.error("Failed to fetch pending disbursements:", error);
    }
  };

  const handleStripeOnboard = async () => {
    setOnboardingLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deal-wallet/stripe/connect/onboard`,
        {
          return_url: `${window.location.origin}/title/escrow?stripe=connected`,
          refresh_url: `${window.location.origin}/title/escrow?stripe=refresh`
        },
        { withCredentials: true }
      );
      
      if (response.data.already_connected) {
        toast.success("Stripe account already connected!");
        fetchStripeStatus();
      } else if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error("Failed to start Stripe onboarding");
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleApproveDisbursement = async (transactionId, notes) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deal-wallet/disbursement/approve`,
        { transaction_id: transactionId, approved: true, notes },
        { withCredentials: true }
      );
      toast.success(response.data.message || "Disbursement approved. Click Execute to release funds.");
      fetchDashboard();
      fetchPendingDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve disbursement");
    }
  };

  const handleRejectDisbursement = async (transactionId, notes) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deal-wallet/disbursement/approve`,
        { transaction_id: transactionId, approved: false, notes },
        { withCredentials: true }
      );
      toast.success(response.data.message || "Disbursement rejected.");
      fetchPendingDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject disbursement");
    }
  };

  const handleExecuteDisbursement = async (transactionId, notes) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/deal-wallet/disbursement/execute`,
        { transaction_id: transactionId, notes },
        { withCredentials: true }
      );
      toast.success(response.data.message || "Funds released.");
      fetchDashboard();
      fetchPendingDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute disbursement");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6" data-testid="title-escrow-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#D4AF37]" />
              Deal Wallet Management
            </h1>
            <p className="text-white/50 mt-1">
              {dashboard?.company_name || "Title Company"} • Fund Execution Authority
            </p>
          </div>
          <Button
            onClick={() => { fetchDashboard(); fetchPendingDisbursements(); }}
            className="bg-white/5 hover:bg-white/10 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stripe Connect Status */}
        {stripeStatus && !stripeStatus.onboarding_complete && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-amber-400" />
                <div>
                  <h3 className="text-amber-400 font-bold">Stripe Connect Required</h3>
                  <p className="text-amber-400/70 text-sm">
                    Connect your Stripe account to receive escrow deposits directly.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleStripeOnboard}
                disabled={onboardingLoading}
                className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
              >
                {onboardingLoading ? "Loading..." : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect Stripe
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {stripeStatus?.onboarding_complete && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              <div>
                <h3 className="text-emerald-400 font-bold">Stripe Connected</h3>
                <p className="text-emerald-400/70 text-sm">
                  Your account is ready to receive deal wallet deposits.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        {dashboard?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white/50 text-sm">Total Wallet Balance</span>
              </div>
              <p className="text-2xl font-bold text-[#D4AF37]">
                {formatCurrency(dashboard.summary.total_wallet_balance ?? dashboard.summary.total_escrow_balance)}
              </p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                <span className="text-white/50 text-sm">Total Deposits</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">
                {formatCurrency(dashboard.summary.total_deposits_received)}
              </p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDownRight className="w-5 h-5 text-blue-400" />
                <span className="text-white/50 text-sm">Total Disbursements</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">
                {formatCurrency(dashboard.summary.total_disbursements_made)}
              </p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-400" />
                <span className="text-white/50 text-sm">Pending Approvals</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">
                {dashboard.summary.pending_disbursements}
              </p>
            </div>
          </div>
        )}

        {/* Pending Disbursements — Two-Step Flow */}
        {pendingDisbursements.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Disbursements Requiring Action ({pendingDisbursements.length})
            </h2>
            <div className="bg-[#111111] border border-amber-500/30 rounded-lg overflow-hidden">
              <div className="divide-y divide-white/[0.06]">
                {pendingDisbursements.map((d) => (
                  <div key={d.transaction_id} className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${d.status === "approved" ? "bg-blue-500/10" : "bg-amber-500/10"}`}>
                          <DollarSign className={`w-5 h-5 ${d.status === "approved" ? "text-blue-400" : "text-amber-400"}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{d.recipient_name}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${d.status === "approved" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-amber-500/10 text-amber-400 border border-amber-500/20"}`}>
                              {d.status === "approved" ? "Approved — Ready to Execute" : "Pending Approval"}
                            </span>
                          </div>
                          <p className="text-white/50 text-sm">
                            {d.property_address || d.deal_id} · {d.payment_type?.replace(/_/g, ' ')}
                          </p>
                          {d.approved_by_name && (
                            <p className="text-white/30 text-xs mt-0.5">
                              Approved by {d.approved_by_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-2">
                          <p className="text-xl font-bold text-white">{formatCurrency(d.amount)}</p>
                          <p className="text-white/40 text-xs">Net: {formatCurrency(d.net_payout)}</p>
                        </div>
                        {d.status === "pending" ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleRejectDisbursement(d.transaction_id, "")}
                              className="bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-500/20"
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => { setSelectedDisbursement(d); setShowApprovalModal(true); }}
                              className="bg-amber-600 hover:bg-amber-500 text-black font-bold"
                              data-testid={`approve-btn-${d.transaction_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleExecuteDisbursement(d.transaction_id, "")}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
                            data-testid={`execute-btn-${d.transaction_id}`}
                          >
                            <ArrowUpRight className="w-4 h-4 mr-1" /> Execute Disbursement
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Deal Wallet Accounts List */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#D4AF37]" />
            Deal Wallets ({dashboard?.deal_accounts?.length ?? dashboard?.escrow_accounts?.length ?? 0})
          </h2>

          {!(dashboard?.deal_accounts?.length ?? dashboard?.escrow_accounts?.length) ? (
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg p-12 text-center">
              <Shield className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <p className="text-white/50">No deal wallets yet</p>
              <p className="text-white/30 text-sm mt-1">
                Deal wallets will appear here when deals are assigned to your company
              </p>
            </div>
          ) : (
            <div className="bg-[#111111] border border-white/[0.08] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/50 text-xs font-medium p-4">PROPERTY</th>
                    <th className="text-left text-white/50 text-xs font-medium p-4">DEAL ID</th>
                    <th className="text-right text-white/50 text-xs font-medium p-4">BALANCE</th>
                    <th className="text-right text-white/50 text-xs font-medium p-4">DEPOSITS</th>
                    <th className="text-right text-white/50 text-xs font-medium p-4">DISBURSED</th>
                    <th className="text-center text-white/50 text-xs font-medium p-4">STATUS</th>
                    <th className="text-right text-white/50 text-xs font-medium p-4">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {(dashboard?.deal_accounts ?? dashboard?.escrow_accounts ?? []).map((acct) => (
                    <tr key={acct.escrow_id} className="hover:bg-white/[0.02]">
                      <td className="p-4">
                        <p className="text-white font-medium">{acct.property_address || "—"}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-white/50 text-sm font-mono">{acct.deal_id}</p>
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-[#D4AF37] font-bold">{formatCurrency(acct.balance)}</p>
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-emerald-400">{formatCurrency(acct.total_deposits)}</p>
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-blue-400">{formatCurrency(acct.total_disbursements)}</p>
                      </td>
                      <td className="p-4 text-center">
                        <StatusBadge status={acct.status} />
                      </td>
                      <td className="p-4 text-right">
                        <Link
                          to={`/title/escrow/${acct.deal_id}`}
                          className="text-[#D4AF37] hover:text-[#D4AF37]/80 text-sm font-medium"
                        >
                          View Ledger →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Approval Modal */}
        <DisbursementApprovalModal
          disbursement={selectedDisbursement}
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedDisbursement(null);
          }}
          onApprove={handleApproveDisbursement}
          onReject={handleRejectDisbursement}
        />
      </div>
    </div>
  );
}
